import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["itemCheckbox", "selectAll", "actionBar", "count"]

    connect() {
        console.log("Bulk selection controller connected")
        this.updateActionBar()
    }

    // This ensures that as Turbo loads or replaces content, we stay synced
    itemCheckboxTargetConnected() {
        this.toggleSingle()
    }

    itemCheckboxTargetDisconnected() {
        this.toggleSingle()
    }

    toggleAll(event) {
        const checked = event.target.checked
        this.itemCheckboxTargets.forEach(checkbox => {
            checkbox.checked = checked
        })
        this.updateActionBar()
    }

    toggleSingle() {
        const total = this.itemCheckboxTargets.length
        const checked = this.itemCheckboxTargets.filter(c => c.checked).length

        const allChecked = total > 0 && checked === total
        const someChecked = checked > 0 && checked < total

        if (this.hasSelectAllTarget) {
            this.selectAllTarget.checked = allChecked
            this.selectAllTarget.indeterminate = someChecked
        }

        this.updateActionBar()
    }

    updateActionBar() {
        const selectedCount = this.selectedIds.length

        if (this.hasActionBarTarget) {
            if (selectedCount > 0) {
                this.actionBarTarget.classList.remove("translate-y-full", "opacity-0")
                this.actionBarTarget.classList.add("translate-y-0", "opacity-100")
                if (this.hasCountTarget) {
                    this.countTarget.textContent = `${selectedCount} item${selectedCount === 1 ? '' : 's'} selected`
                }
            } else {
                this.actionBarTarget.classList.add("translate-y-full", "opacity-0")
                this.actionBarTarget.classList.remove("translate-y-0", "opacity-100")
            }
        }
    }

    async addSelectedToGroup(event) {
        const groupId = event.target.value
        if (!groupId) return

        const url = this.isCustomerContext ? "/customers/bulk_add_to_group" : "/products/bulk_add_to_group"

        if (confirm(`Add ${this.selectedIds.length} items to this group?`)) {
            await this.performBulkAction(url, { group_id: groupId })
        }
        event.target.value = "" // Reset select
    }

    async removeSelectedFromGroup(event) {
        const groupId = event.target.value
        if (!groupId) return

        const url = this.isCustomerContext ? "/customers/bulk_remove_from_group" : "/products/bulk_remove_from_group"

        if (confirm(`Remove ${this.selectedIds.length} items from this group?`)) {
            await this.performBulkAction(url, { group_id: groupId })
        }
        event.target.value = "" // Reset select
    }

    async removeSelectedFromCurrentGroup() {
        const groupId = document.getElementById("current_group_id")?.value ||
            document.getElementById("current_customer_group_id")?.value
        if (!groupId) return

        const url = this.isCustomerContext ? "/customers/bulk_remove_from_group" : "/products/bulk_remove_from_group"

        if (confirm(`Remove ${this.selectedIds.length} items from this group?`)) {
            await this.performBulkAction(url, { group_id: groupId })
        }
    }

    async deleteSelected() {
        const url = this.isCustomerContext ? "/customers/bulk_destroy" : "/products/bulk_destroy"

        if (confirm(`Are you sure you want to delete ${this.selectedIds.length} items? This cannot be undone.`)) {
            await this.performBulkAction(url, {}, "DELETE")
        }
    }

    async performBulkAction(url, extraParams = {}, method = "POST") {
        const ids = this.selectedIds
        const idKey = this.isCustomerContext ? "customer_ids" : "product_ids"

        this.showLoading()

        try {
            const body = {
                [idKey]: ids,
                ...extraParams
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify(body)
            })

            const data = await response.json()

            if (response.ok) {
                // If it's a background job, show a message instead of reloading immediately
                if (data.async) {
                    this.showToast(data.message || "Action started in background", "notice")

                    // Remove selected rows from the DOM for immediate feedback
                    this.itemCheckboxTargets.forEach(checkbox => {
                        if (checkbox.checked) {
                            const row = checkbox.closest('.table-row') || checkbox.closest('tr')
                            if (row) {
                                row.classList.add('opacity-0', 'transition-opacity', 'duration-500')
                                setTimeout(() => row.remove(), 500)
                            }
                        }
                    })

                    this.hideLoading()
                    this.updateActionBar()
                } else {
                    window.location.reload()
                }
            } else {
                this.hideLoading()
                this.showToast(data.message || "Action failed", "alert")
            }
        } catch (error) {
            this.hideLoading()
            console.error('Bulk action failed:', error)

            let userMessage = "An error occurred while processing your request."

            if (error.message && (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))) {
                userMessage = "Network error. Please check your connection and try again."
            } else if (error.message && error.message.includes('timeout')) {
                userMessage = "Request timed out. Please try again."
            }

            this.showToast(userMessage, "alert")
        }
    }

    showLoading() {
        if (this.hasActionBarTarget) {
            const buttons = this.actionBarTarget.querySelectorAll('button, select')
            buttons.forEach(b => b.disabled = true)

            const deleteBtn = this.actionBarTarget.querySelector('[data-action="click->bulk-selection#deleteSelected"]')
            if (deleteBtn) {
                const count = this.selectedIds.length
                this.originalDeleteText = deleteBtn.innerHTML
                deleteBtn.innerHTML = `
                    <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting ${count} items...
                `
            }
        }

        this.itemCheckboxTargets.forEach(checkbox => {
            checkbox.disabled = true
            if (checkbox.checked) {
                const row = checkbox.closest('.table-row') || checkbox.closest('tr')
                if (row) row.classList.add('opacity-50')
            }
        })
        if (this.hasSelectAllTarget) this.selectAllTarget.disabled = true
    }

    hideLoading() {
        if (this.hasActionBarTarget) {
            const buttons = this.actionBarTarget.querySelectorAll('button, select')
            buttons.forEach(b => b.disabled = false)

            const deleteBtn = this.actionBarTarget.querySelector('[data-action="click->bulk-selection#deleteSelected"]')
            if (deleteBtn && this.originalDeleteText) {
                deleteBtn.innerHTML = this.originalDeleteText
            }
        }

        this.itemCheckboxTargets.forEach(checkbox => {
            checkbox.disabled = false
            const row = checkbox.closest('.table-row') || checkbox.closest('tr')
            if (row) row.classList.remove('opacity-50')
        })
        if (this.hasSelectAllTarget) this.selectAllTarget.disabled = false
    }

    showToast(message, type = "notice") {
        const container = document.getElementById('toasts')
        if (!container) return

        const toast = document.createElement('div')
        const toastClass = type === "notice" ? "toast-notice" : "toast-alert"
        const iconSvg = type === "notice"
            ? '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>'
            : '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>'

        toast.className = `toast ${toastClass} transform transition-all duration-300 ease-in-out -translate-y-full opacity-0`
        toast.setAttribute('data-controller', 'toast')
        toast.setAttribute('data-action', 'click->toast#close')

        toast.innerHTML = `
            <div class="toast-icon">${iconSvg}</div>
            <div class="toast-content" style="flex: 1; font-weight: 500;">${message}</div>
        `

        container.appendChild(toast)

        // Trigger animation in next tick
        setTimeout(() => {
            toast.classList.remove('-translate-y-full', 'opacity-0')
            toast.classList.add('translate-y-0', 'opacity-100')
        }, 10)
    }

    get selectedIds() {
        return this.itemCheckboxTargets
            .filter(checkbox => checkbox.checked)
            .map(checkbox => checkbox.value)
    }

    get isCustomerContext() {
        return !!document.getElementById("current_customer_group_id") ||
            !!document.getElementById("current_customer_page") ||
            window.location.pathname.includes("/customers")
    }
}
